/**
 * VerificationFilters — Filter controls for AdminVerificationsTab.
 * Owns nothing but the rendering — every piece of state and every handler
 * is passed in from the parent, keeping this fully controlled and easy
 * to test in isolation.
 */
import { ShieldCheck, ShieldX, BadgeCheck, Loader2, Square, CheckSquare } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

export const STATUS_TABS = [
  { value: "pending", label: "Pending", icon: ShieldX },
  { value: "verified", label: "Verified", icon: ShieldCheck },
  { value: "all", label: "All", icon: BadgeCheck },
];

export const SOURCE_FILTERS = [
  { value: "all", label: "All sources" },
  { value: "google_places", label: "Google Places" },
  { value: "manual", label: "Manual" },
];

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export function StatusTabsList() {
  return (
    <TabsList className="mb-5" data-testid="admin-verifications-status-tabs">
      {STATUS_TABS.map((t) => {
        const Icon = t.icon;
        return (
          <TabsTrigger
            key={t.value}
            value={t.value}
            className="flex items-center gap-2"
            data-testid={`admin-verifications-tab-${t.value}`}
          >
            <Icon className="w-4 h-4" />
            {t.label}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

export function AlphabetFilter({ letter, onChange }) {
  const btnCls = (active) =>
    `text-xs rounded-md border transition-colors font-medium ${
      active
        ? "bg-tbc-gold text-tbc-navy border-tbc-gold"
        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
    }`;
  return (
    <div className="flex flex-wrap items-center gap-1 mb-4" data-testid="admin-verifications-alphabet-filter">
      <span className="text-xs uppercase tracking-wider text-gray-400 mr-2">Jump to:</span>
      <button
        type="button"
        onClick={() => onChange("")}
        className={`px-2.5 py-1 ${btnCls(letter === "")} ${letter === "" ? "bg-tbc-navy text-white border-tbc-navy" : ""}`}
        data-testid="alphabet-filter-all"
      >
        All
      </button>
      {ALPHABET.map((L) => (
        <button
          key={L}
          type="button"
          onClick={() => onChange(letter === L ? "" : L)}
          className={`w-7 h-7 ${btnCls(letter === L)}`}
          data-testid={`alphabet-filter-${L}`}
        >
          {L}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(letter === "#" ? "" : "#")}
        className={`w-7 h-7 ${btnCls(letter === "#")}`}
        title="Names starting with a number or symbol"
        data-testid="alphabet-filter-hash"
      >
        #
      </button>
      {letter && (
        <span className="ml-2 text-xs text-tbc-navy/70" data-testid="alphabet-filter-active">
          Showing names starting with <strong>{letter}</strong>
        </span>
      )}
    </div>
  );
}

export function SourceFilterBar({
  sourceFilter,
  onSourceChange,
  status,
  businesses,
  selected,
  bulkBusy,
  onVerifySelected,
  onVerifyAllOfSource,
}) {
  const sourceLabel =
    sourceFilter === "google_places"
      ? "Google Places businesses"
      : sourceFilter === "manual"
      ? "manually-added businesses"
      : "pending businesses";
  const verifyAllLabel =
    sourceFilter === "google_places"
      ? "Verify all Google Places"
      : sourceFilter === "manual"
      ? "Verify all Manual"
      : "Verify all pending";
  return (
    <div
      className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100"
      data-testid="admin-bulk-verify-toolbar"
    >
      <div className="flex items-center gap-1.5 text-xs text-gray-500 mr-2">Source:</div>
      {SOURCE_FILTERS.map((f) => (
        <button
          key={f.value}
          onClick={() => onSourceChange(f.value)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            sourceFilter === f.value
              ? "bg-tbc-navy text-white border-tbc-navy"
              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
          }`}
          data-testid={`source-filter-${f.value}`}
        >
          {f.label}
        </button>
      ))}
      {status === "pending" && businesses.length > 0 && (
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {selected.size > 0 && (
            <Button
              size="sm"
              onClick={onVerifySelected}
              disabled={bulkBusy}
              className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
              data-testid="bulk-verify-selected"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
              Verify {selected.size} selected
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => onVerifyAllOfSource(sourceLabel)}
            disabled={bulkBusy}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="bulk-verify-all-source"
          >
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
            {verifyAllLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export function SelectAllToggle({ allSelectedOnPage, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="text-xs text-tbc-navy hover:underline flex items-center gap-1.5"
      data-testid="bulk-select-toggle-all"
    >
      {allSelectedOnPage ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
      {allSelectedOnPage ? "Clear selection" : "Select all on this page"}
    </button>
  );
}
